import { jsPDF } from "jspdf";
import { CONFIG, dibujarEtiqueta } from "./ImpresionEtiqueta.js";

// ─────────────────────────────────────────────────────────────────────────────
// CONSTRUIR URL DE MEDIA
// ─────────────────────────────────────────────────────────────────────────────
const MEDIA_BASE = "https://sigaf.tamep.bo";
const buildQrUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http")) return path.replace("siaem.tamep.bo", "sigaf.tamep.bo");
  return `${MEDIA_BASE}${path}`;
};

// ─────────────────────────────────────────────────────────────────────────────
// FUNCIÓN PRINCIPAL EXPORTADA (MODO MINIMALISTA)
// ─────────────────────────────────────────────────────────────────────────────
export const imprimirEtiquetasMasivas = async (activosBackend) => {
  const { default: Swal } = await import("sweetalert2");

  // Filtrar solo activos que tienen QR generado
  const lista = (activosBackend || [])
    .filter((a) => a.imagen_qr)
    .map((a) => ({
      qrUrl:        buildQrUrl(a.imagen_qr),
      codigo_tamep: a.codigo_tamep,
      codigo_tab:   a.codigo_tab,
      serie:        a.serie,
    }));

  if (lista.length === 0) {
    Swal.fire({
      title: "Sin etiquetas",
      text: "Ninguno de los activos filtrados tiene código QR generado.",
      icon: "warning",
      showClass: { popup: "" },
      hideClass: { popup: "" },
    });
    return;
  }

  const paginasA4 = Math.ceil(lista.length / CONFIG.totalSlots);
  const ultimoFormato = localStorage.getItem("tamep_pref_metodo_impresion") || "termica";

  const { value: formato } = await Swal.fire({
    title: "Impresión Masiva de Etiquetas",
    showClass: { popup: "" },
    hideClass: { popup: "" },
    html: `
      <div style="text-align:left;font-size:13px;line-height:1.5;color:#334155;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;">
        <p style="margin:0 0 12px;color:#64748b;font-size:12px;">
          Se procesarán <strong>${lista.length} etiquetas</strong> con código QR.
        </p>
        
        <div style="display:flex;flex-direction:column;gap:8px;margin:12px 0;">
          
          <!-- Opción Térmica -->
          <label id="lbl-termica" style="
            display:flex;align-items:center;justify-content:space-between;gap:10px;padding:12px 14px;
            border:1px solid ${ultimoFormato === 'termica' ? '#003366' : '#cbd5e1'};
            background:${ultimoFormato === 'termica' ? '#f0f7ff' : '#ffffff'};
            border-radius:8px;cursor:pointer;
          ">
            <div style="display:flex;align-items:center;gap:10px;">
              <input type="radio" name="swal_formato" value="termica" ${ultimoFormato === 'termica' ? 'checked' : ''} style="accent-color:#003366;">
              <div>
                <strong style="color:${ultimoFormato === 'termica' ? '#003366' : '#0f172a'};font-size:13px;display:block;">
                  🏷️ Rollo Térmico (50×25 mm)
                </strong>
                <span style="font-size:11px;color:#64748b;display:block;">
                  Rollo continuo para impresoras Zebra, Xprinter, TSC
                </span>
              </div>
            </div>
            <span style="background:#e0f2fe;color:#0369a1;font-size:10px;font-weight:700;padding:2px 8px;border-radius:4px;white-space:nowrap;">
              ${lista.length} etiquetas
            </span>
          </label>

          <!-- Opción A4 -->
          <label id="lbl-a4" style="
            display:flex;align-items:center;justify-content:space-between;gap:10px;padding:12px 14px;
            border:1px solid ${ultimoFormato === 'a4' ? '#003366' : '#cbd5e1'};
            background:${ultimoFormato === 'a4' ? '#f0f7ff' : '#ffffff'};
            border-radius:8px;cursor:pointer;
          ">
            <div style="display:flex;align-items:center;gap:10px;">
              <input type="radio" name="swal_formato" value="a4" ${ultimoFormato === 'a4' ? 'checked' : ''} style="accent-color:#003366;">
              <div>
                <strong style="color:${ultimoFormato === 'a4' ? '#003366' : '#0f172a'};font-size:13px;display:block;">
                  📄 Hojas A4 Adhesivas (Grilla 4×11)
                </strong>
                <span style="font-size:11px;color:#64748b;display:block;">
                  44 etiquetas por hoja para impresoras Epson, Láser
                </span>
              </div>
            </div>
            <span style="background:#f1f5f9;color:#475569;font-size:10px;font-weight:700;padding:2px 8px;border-radius:4px;white-space:nowrap;">
              ${paginasA4} pág(s)
            </span>
          </label>
        </div>

        ${activosBackend.length > lista.length ? `
          <div style="margin-top:8px;padding:6px 10px;background:#fef2f2;border-radius:6px;border:1px solid #fecaca;">
            <small style="color:#b91c1c;font-size:11px;">
              ⚠️ ${activosBackend.length - lista.length} activo(s) omitidos por falta de código QR.
            </small>
          </div>
        ` : ''}
      </div>
    `,
    didOpen: () => {
      const radios = document.querySelectorAll('input[name="swal_formato"]');
      radios.forEach((r) => {
        r.addEventListener("change", () => {
          radios.forEach((rb) => {
            const lbl = rb.closest("label");
            const strong = lbl.querySelector("strong");
            if (rb.checked) {
              lbl.style.borderColor = "#003366";
              lbl.style.background = "#f0f7ff";
              if (strong) strong.style.color = "#003366";
            } else {
              lbl.style.borderColor = "#cbd5e1";
              lbl.style.background = "#ffffff";
              if (strong) strong.style.color = "#0f172a";
            }
          });
        });
      });
    },
    preConfirm: () => {
      const checked = document.querySelector('input[name="swal_formato"]:checked');
      return checked ? checked.value : "termica";
    },
    showCancelButton: true,
    confirmButtonColor: "#003366",
    cancelButtonColor: "#64748b",
    confirmButtonText: "Generar Impresión",
    cancelButtonText: "Cancelar",
  });

  if (!formato) return;

  localStorage.setItem("tamep_pref_metodo_impresion", formato);

  if (formato === "termica") {
    // ── Modo Térmico: 1 etiqueta de 50x25 mm por página ──
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: [25, 50] });

    lista.forEach((activo, index) => {
      if (index > 0) {
        doc.addPage([25, 50], "landscape");
      }
      dibujarEtiqueta(doc, activo, 0, 0, true);
    });

    doc.autoPrint();
    const pdfUrl = doc.output("bloburl");
    const nueva = window.open(pdfUrl, "_blank");

    if (!nueva || nueva.closed || typeof nueva.closed === "undefined") {
      Swal.fire({
        title: "Ventana bloqueada",
        text: "Tu navegador bloqueó el PDF. Permite las ventanas emergentes para SIGAF.",
        icon: "warning",
        confirmButtonText: "Entendido",
        showClass: { popup: "" },
        hideClass: { popup: "" },
      });
    }
  } else {
    // ── Modo A4: Grilla 4x11 en hojas A4 ──
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

    lista.forEach((activo, index) => {
      const slotEnPagina = index % CONFIG.totalSlots;

      if (slotEnPagina === 0 && index > 0) {
        doc.addPage();
      }

      const col = slotEnPagina % CONFIG.cols;
      const row = Math.floor(slotEnPagina / CONFIG.cols);
      const lx = CONFIG.marginLeft + col * (CONFIG.labelW + CONFIG.gapX);
      const ly = CONFIG.marginTop + row * (CONFIG.labelH + CONFIG.gapY);

      dibujarEtiqueta(doc, activo, lx, ly, false);
    });

    doc.autoPrint();
    const pdfUrl = doc.output("bloburl");
    const nueva = window.open(pdfUrl, "_blank");

    if (!nueva || nueva.closed || typeof nueva.closed === "undefined") {
      Swal.fire({
        title: "Ventana bloqueada",
        text: "Tu navegador bloqueó el PDF. Permite las ventanas emergentes para SIGAF.",
        icon: "warning",
        confirmButtonText: "Entendido",
        showClass: { popup: "" },
        hideClass: { popup: "" },
      });
    }
  }
};
